/**
 * A statement served as an image — a geometry figure, a science diagram
 * (`exercise` §19 bis).
 *
 * Three things this component owes the child, none of them decoration:
 *
 * * **The text is the `alt`.** §19 bis.6 says the exercise carries its text as
 *   well as its picture; a screen reader that is handed "énoncé de l'exercice"
 *   is handed nothing. The text stays *visually* hidden — the child is meant to
 *   read the picture, figure included — but it is what the image announces.
 * * **The room is booked before the picture arrives.** 250 kB over 4G is not
 *   instant, and a layout that jumps when it lands costs a child their line.
 *   The frame is sized from the declared pixel size, or from a default ratio
 *   when the API did not send one.
 * * **A picture that never comes is said out loud**, in French, with a way out:
 *   retry, and — only if asked for — the statement in text, without the figure.
 *
 * Zoom is the gesture everyone knows from a PDF: pinch, drag, and **double tap
 * to come back**. §19 bis.7 asks for it on the phone; on a tablet the whole
 * image already fits, so nothing has to be done to read it.
 *
 * Every decision about what the view *is* lives in the pure functions below —
 * `frameFor`, `clampView`, `zoomTo`, `toggleZoom` — so the arithmetic that makes
 * or breaks the gesture is tested without a DOM, exactly as `Gauge.tsx` does.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { Button } from "@/ui/primitives";

// --- What the API is expected to send ---------------------------------------

/**
 * The picture of a statement, as the child shell needs it.
 *
 * Local on purpose: `api/schema.ts` is generated from `core-api`'s OpenAPI and
 * this field does not exist there yet. What it expects, in the payload of an
 * `exercise.served` event, next to `statement`, `answer` and `hints`:
 *
 * ```json
 * "statement_image": { "url": "https://…", "width": 1704, "height": 980 }
 * ```
 *
 * `url` is a presigned S3 URL for the **statement** — the `statement_image_key`
 * column of `exercise_exercise`, signed at serve time; never the corrigé, whose
 * own URL is only issued once an attempt is written (§19 bis.7). `width` and
 * `height` are the natural pixel size; they are optional, but without them the
 * frame is reserved from a default ratio and the layout may still shift by a
 * few pixels when the picture lands. A bare string URL is accepted too.
 */
export type StatementImage = {
  url: string;
  width: number | null;
  height: number | null;
};

/** Reads the field above out of an untyped event payload; null when absent. */
export function readStatementImage(payload: unknown): StatementImage | null {
  const raw = (payload as Record<string, unknown> | null)?.statement_image;
  if (typeof raw === "string") {
    return raw.trim() ? { url: raw.trim(), width: null, height: null } : null;
  }
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const url = typeof record.url === "string" ? record.url.trim() : "";
  if (!url) return null;
  const size = (value: unknown): number | null =>
    typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
  return { url, width: size(record.width), height: size(record.height) };
}

/**
 * La même image, quand l'URL arrive **déjà signée dans une réponse typée**.
 *
 * `readStatementImage` fouille une charge non typée — celle d'un événement SSE
 * de séance. Les routes ordinaires, elles, portent un `statement_image_url`
 * déclaré au contrat et sans dimensions : `ExerciseOut`, `WorkedExerciseOut`,
 * `ExercicePrevu`. Trois écrans en fabriquaient l'objet à la main, avec la même
 * ligne recopiée — et le jour où le contrat portera la taille, une copie
 * oubliée laisserait un écran réserver son cadre au petit bonheur.
 */
export function imageDeLEnonce(url: string | null | undefined): StatementImage | null {
  const propre = (url ?? "").trim();
  return propre ? { url: propre, width: null, height: null } : null;
}

/**
 * Toutes les pages d'un énoncé ou d'un corrigé découpé, dans l'ordre imprimé.
 *
 * Un exercice de cahier qui passe d'une colonne à l'autre est découpé en
 * plusieurs rectangles ; le contrat porte alors une liste (`…_urls`) **à côté**
 * du champ singulier, qui reste la première page pour les coquilles installées.
 * La liste l'emporte quand elle est remplie ; sinon on retombe sur le singulier
 * — un serveur plus ancien, ou un exercice à une seule page.
 */
export function imagesDeLEnonce(
  urls: readonly (string | null | undefined)[] | null | undefined,
  url?: string | null,
): StatementImage[] {
  const pages = (urls ?? []).map(imageDeLEnonce).filter((image): image is StatementImage => image !== null);
  if (pages.length) return pages;
  const seule = imageDeLEnonce(url);
  return seule ? [seule] : [];
}

/**
 * Les pages d'un énoncé dans une charge d'événement de séance : `statement_images`
 * (liste d'URL) quand elle est là, sinon la seule `statement_image`.
 */
export function readStatementImages(payload: unknown): StatementImage[] {
  const raw = (payload as Record<string, unknown> | null)?.statement_images;
  if (Array.isArray(raw)) {
    const pages = imagesDeLEnonce(raw.filter((value): value is string => typeof value === "string"));
    if (pages.length) return pages;
  }
  const seule = readStatementImage(payload);
  return seule ? [seule] : [];
}

/** « partie 2 sur 3 », ou rien quand il n'y a qu'une page. */
export function partieDe(rang: number, total: number): string | undefined {
  return total > 1 ? `partie ${rang + 1} sur ${total}` : undefined;
}

// --- The view, as arithmetic -------------------------------------------------

/** Fit-to-width is 1. Four times that is already a single fraction filling a phone. */
export const MAX_ZOOM = 4;
export const DOUBLE_TAP_ZOOM = 2.5;
/** Two taps further apart than this are two taps, not a double tap. */
export const DOUBLE_TAP_MS = 320;
/** A child's finger never lands twice on the same pixel. */
export const DOUBLE_TAP_SLOP = 36;
/** Used only when the API did not declare the picture's size. */
const DEFAULT_RATIO = 4 / 3;
/** Below this, a scale is fit-to-width and the picture is "not zoomed". */
const ZOOMED_ABOVE = 1.02;

export type Size = { width: number; height: number };

export type Frame = {
  /** The window cut into the page. */
  width: number;
  height: number;
  /** The picture at scale 1, which may be taller than the window. */
  content: Size;
  /** True when the whole picture is visible without touching anything. */
  whole: boolean;
};

/** Scale is relative to fit-to-width; `x`/`y` are frame pixels, origin top-left. */
export type View = { scale: number; x: number; y: number };

export const FIT: View = { scale: 1, x: 0, y: 0 };

/**
 * The window and the picture inside it.
 *
 * Fit-to-width, capped in height: on Baya's tablet the cap does not bite and
 * the whole image shows (`whole`), on Mehdi's phone a 1 700 px wide scan lands
 * legible-width but tall, and that is what the gesture is for.
 */
export function frameFor(image: Size | null, available: number, maxHeight: number): Frame {
  const width = Math.max(0, available);
  const ratio = image && image.width > 0 && image.height > 0 ? image.height / image.width : 1 / DEFAULT_RATIO;
  const content = { width, height: width * ratio };
  const height = Math.min(content.height, Math.max(0, maxHeight));
  return { width, height, content, whole: content.height <= height + 0.5 };
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value));

/** Centred when it is smaller than the window, never dragged off the edge otherwise. */
function place(offset: number, extent: number, window: number): number {
  if (extent <= window) return (window - extent) / 2;
  return clamp(offset, window - extent, 0);
}

export function clampView(view: View, frame: Frame): View {
  const scale = clamp(view.scale, 1, MAX_ZOOM);
  return {
    scale,
    x: place(view.x, frame.content.width * scale, frame.width),
    y: place(view.y, frame.content.height * scale, frame.height),
  };
}

/** Zoom keeping `focal` — a frame coordinate — under the same bit of picture. */
export function zoomTo(view: View, frame: Frame, next: number, focal: { x: number; y: number }): View {
  const scale = clamp(next, 1, MAX_ZOOM);
  const factor = scale / view.scale;
  return clampView(
    {
      scale,
      x: focal.x - (focal.x - view.x) * factor,
      y: focal.y - (focal.y - view.y) * factor,
    },
    frame,
  );
}

/** `ratio` is the pinch: current finger distance over the previous one. */
export function pinch(view: View, frame: Frame, ratio: number, focal: { x: number; y: number }): View {
  if (!Number.isFinite(ratio) || ratio <= 0) return view;
  return zoomTo(view, frame, view.scale * ratio, focal);
}

export function panBy(view: View, frame: Frame, dx: number, dy: number): View {
  return clampView({ ...view, x: view.x + dx, y: view.y + dy }, frame);
}

export const isZoomed = (view: View): boolean => view.scale > ZOOMED_ABOVE;

/**
 * Double tap: the way out.
 *
 * Zoomed in, it goes back to the whole picture — the child who got lost in the
 * corner of a figure gets out with the gesture they already tried. Not zoomed,
 * it magnifies where the finger landed.
 */
export function toggleZoom(view: View, frame: Frame, focal: { x: number; y: number }): View {
  return isZoomed(view) ? clampView(FIT, frame) : zoomTo(view, frame, DOUBLE_TAP_ZOOM, focal);
}

export type Tap = { at: number; x: number; y: number };

export function isDoubleTap(previous: Tap | null, tap: Tap): boolean {
  if (!previous) return false;
  return (
    tap.at - previous.at <= DOUBLE_TAP_MS &&
    Math.hypot(tap.x - previous.x, tap.y - previous.y) <= DOUBLE_TAP_SLOP
  );
}

/**
 * Who owns a one-finger drag: the picture, or the page under it.
 *
 * The picture only takes it when there is something to move — zoomed in, or
 * taller than its window. Otherwise the page keeps its scroll, so a child who
 * just wanted to reach the keyboard below the statement is not trapped by an
 * image that had nothing to show them.
 */
export const touchActionFor = (view: View, frame: Frame): "none" | "pan-y" =>
  isZoomed(view) || !frame.whole ? "none" : "pan-y";

/**
 * What the image announces to a screen reader.
 *
 * The statement's own text (§19 bis.6), never a label naming the widget: "énoncé
 * de l'exercice" tells a child using a screen reader precisely nothing.
 */
export function altFor(statement: string, partie?: string): string {
  const text = statement.trim();
  const base = text || "Énoncé en image. Le texte n'est pas disponible.";
  return partie ? `${base} (${partie})` : base;
}

// --- The view ----------------------------------------------------------------

/** Share of the viewport a statement may take before the child has to pan. */
const MAX_FRAME_SHARE = 0.62;
const MIN_FRAME_HEIGHT = 220;

type Load = "loading" | "ready" | "failed";

/**
 * Plusieurs pages l'une sous l'autre, chacune avec son propre zoom : un énoncé
 * découpé en deux colonnes se lit comme dans le cahier, de haut en bas.
 */
export function EnonceImages({
  images,
  statement,
}: {
  images: readonly StatementImage[];
  statement: string;
}) {
  return (
    <>
      {images.map((image, rang) => (
        <EnonceImage
          key={`${rang}-${image.url}`}
          image={image}
          statement={statement}
          partie={partieDe(rang, images.length)}
        />
      ))}
    </>
  );
}

export function EnonceImage({
  image,
  statement,
  partie,
}: {
  image: StatementImage;
  /** The statement's text. Read by screen readers; never drawn next to the picture. */
  statement: string;
  /** « partie 2 sur 3 » quand l'énoncé tient sur plusieurs images. */
  partie?: string;
}) {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [available, setAvailable] = useState(0);
  const [maxHeight, setMaxHeight] = useState(MIN_FRAME_HEIGHT);
  const [natural, setNatural] = useState<Size | null>(
    image.width && image.height ? { width: image.width, height: image.height } : null,
  );
  const [load, setLoad] = useState<Load>("loading");
  const [attempt, setAttempt] = useState(0);
  const [view, setView] = useState<View>(FIT);
  const [showText, setShowText] = useState(false);

  const frame = useMemo(() => frameFor(natural, available, maxHeight), [natural, available, maxHeight]);

  // --- Measuring ------------------------------------------------------------
  useEffect(() => {
    const element = frameRef.current;
    if (!element) return;
    const measure = () => {
      setAvailable(element.clientWidth);
      setMaxHeight(Math.max(MIN_FRAME_HEIGHT, Math.round(window.innerHeight * MAX_FRAME_SHARE)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // A rotated tablet is a different frame: whatever was on screen stays on it.
  useEffect(() => setView((current) => clampView(current, frame)), [frame]);

  // --- Gestures -------------------------------------------------------------
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const origins = useRef(new Map<number, Tap>());
  const spread = useRef<number | null>(null);
  const lastTap = useRef<Tap | null>(null);
  const frameOf = useRef(frame);
  frameOf.current = frame;

  const local = (event: { clientX: number; clientY: number }) => {
    const box = frameRef.current?.getBoundingClientRect();
    return { x: event.clientX - (box?.left ?? 0), y: event.clientY - (box?.top ?? 0) };
  };

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    const point = local(event);
    pointers.current.set(event.pointerId, point);
    origins.current.set(event.pointerId, { ...point, at: event.timeStamp });
    if (pointers.current.size === 2) spread.current = distance();
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const distance = (): number | null => {
    const [a, b] = [...pointers.current.values()];
    return a && b ? Math.hypot(b.x - a.x, b.y - a.y) : null;
  };

  const centre = (): { x: number; y: number } | null => {
    const [a, b] = [...pointers.current.values()];
    return a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : null;
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const previous = pointers.current.get(event.pointerId);
    if (!previous) return;
    const point = local(event);
    pointers.current.set(event.pointerId, point);

    if (pointers.current.size >= 2) {
      const now = distance();
      const focal = centre();
      if (now && focal && spread.current) {
        const ratio = now / spread.current;
        spread.current = now;
        setView((current) => pinch(current, frameOf.current, ratio, focal));
      }
      return;
    }

    // One finger only drags the picture once it is bigger than its window;
    // otherwise the page keeps its scroll (see `touchActionFor`).
    if (!isZoomed(view) && frameOf.current.whole) return;
    setView((current) => panBy(current, frameOf.current, point.x - previous.x, point.y - previous.y));
  };

  const endPointer = (event: ReactPointerEvent<HTMLDivElement>) => {
    const origin = origins.current.get(event.pointerId);
    pointers.current.delete(event.pointerId);
    origins.current.delete(event.pointerId);
    if (pointers.current.size < 2) spread.current = null;
    if (!origin || pointers.current.size > 0) return;

    const point = local(event);
    const moved = Math.hypot(point.x - origin.x, point.y - origin.y);
    if (moved > DOUBLE_TAP_SLOP) {
      lastTap.current = null;
      return;
    }
    const tap: Tap = { ...point, at: event.timeStamp };
    if (isDoubleTap(lastTap.current, tap)) {
      setView((current) => toggleZoom(current, frameOf.current, tap));
      lastTap.current = null;
    } else {
      lastTap.current = tap;
    }
  };

  const step = useCallback(
    (factor: number) =>
      setView((current) =>
        zoomTo(current, frameOf.current, current.scale * factor, {
          x: frameOf.current.width / 2,
          y: frameOf.current.height / 2,
        }),
      ),
    [],
  );

  const retry = () => {
    setLoad("loading");
    setAttempt((count) => count + 1);
  };

  const zoomed = isZoomed(view);

  return (
    <figure className="enonce-image" data-state={load}>
      <div
        ref={frameRef}
        className="enonce-image__frame"
        // Booked before the picture arrives: nothing below it moves when it does.
        style={{
          height: frame.height ? `${frame.height}px` : undefined,
          touchAction: touchActionFor(view, frame),
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        {load === "failed" ? null : (
          <img
            // A new key forces a fresh request rather than the cached failure.
            key={attempt}
            className="enonce-image__picture"
            src={image.url}
            alt={altFor(statement, partie)}
            draggable={false}
            decoding="async"
            style={{
              width: `${frame.content.width}px`,
              height: `${frame.content.height}px`,
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            }}
            onLoad={(event) => {
              const element = event.currentTarget;
              if (element.naturalWidth && element.naturalHeight) {
                setNatural({ width: element.naturalWidth, height: element.naturalHeight });
              }
              setLoad("ready");
            }}
            onError={() => setLoad("failed")}
          />
        )}

        {load === "loading" ? (
          <p className="enonce-image__waiting" role="status">
            Je charge l'image…
          </p>
        ) : null}
      </div>

      {load === "failed" ? (
        <div className="enonce-image__failed">
          <p role="alert">Je n'arrive pas à afficher l'image de l'exercice.</p>
          <p className="muted small">Vérifie ta connexion, puis réessaie.</p>
          <div className="enonce-image__tools">
            <Button variant="secondary" onClick={retry}>
              Réessayer
            </Button>
            {statement.trim() && !showText ? (
              <Button variant="quiet" onClick={() => setShowText(true)}>
                Lire l'énoncé en texte
              </Button>
            ) : null}
          </div>
          {/* Shown only if asked for: the text has no figure, and §19 bis.6
              keeps it out of sight as long as the picture does its job. */}
          {showText ? <p className="statement">{statement}</p> : null}
        </div>
      ) : (
        <figcaption className="enonce-image__tools">
          <Button variant="quiet" onClick={() => step(1 / 1.5)} disabled={!zoomed} aria-label="Réduire l'image">
            −
          </Button>
          <Button variant="quiet" onClick={() => step(1.5)} aria-label="Agrandir l'image">
            +
          </Button>
          <Button
            variant="quiet"
            onClick={() => setView(clampView(FIT, frame))}
            disabled={!zoomed}
            aria-label="Voir l'image en entier"
          >
            Voir en entier
          </Button>
          <span className="muted small">
            {frame.whole && !zoomed ? "Pince pour agrandir." : "Pince pour zoomer, tape deux fois pour revenir."}
          </span>
        </figcaption>
      )}
    </figure>
  );
}
